import { useEffect, useState } from "react";
import { Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const toKey = (b64) => {
  const s = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
};

export const PushToggle = () => {
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  const ok = supported();

  useEffect(() => {
    if (!ok) return;
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {});
  }, [ok]);

  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") { toast.error("Has bloqueado los avisos. Actívalos en los ajustes del navegador para esta web."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/key");
      const s = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) });
      await api.post("/push/subscribe", s.toJSON());
      await api.post("/push/test");
      setSub(s);
      toast.success("Avisos activados en este dispositivo");
    } catch (e) {
      toast.error("No se han podido activar los avisos en este dispositivo.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try { await api.post("/push/unsubscribe", sub.toJSON()); await sub.unsubscribe(); setSub(null); toast.success("Avisos desactivados"); }
    finally { setBusy(false); }
  };

  if (!ok) {
    if (!isIOS || standalone()) return null;
    return <p className="mt-6 inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm" data-testid="push-ios-hint"><Bell size={16} />Instala la app en tu iPhone para recibir avisos en el móvil.</p>;
  }
  return sub ? (
    <button onClick={disable} disabled={busy} className="mt-6 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-full px-5 py-2.5 font-bold transition-colors" data-testid="push-disable">
      <BellRing size={18} className="text-[#93C5FD]" />Avisos en el móvil activados <span className="font-normal text-blue-200 underline ml-1 inline-flex items-center gap-1"><BellOff size={14} />Desactivar</span>
    </button>
  ) : (
    <button onClick={enable} disabled={busy} className="mt-6 inline-flex items-center gap-2 bg-[#B23A38] hover:bg-[#962F2D] active:scale-95 rounded-full px-5 py-3 font-bold text-white shadow-lg transition-[background-color,transform]" data-testid="push-enable">
      <Bell size={18} />{busy ? "Activando…" : "Activar avisos en el móvil"}
    </button>
  );
};
